import { computed, inject, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import type { ComponentNode } from '@api/types.gen'
import { slotHasVisibleChild, type SlotVisibilityContext } from '@render/lib/slotVisibility'
import { useVisibilityMap } from './useLogic'
import { PipeDisplayMode, usePipe } from './usePipe'

/**
 * The context `slotHasVisibleChild` evaluates `nodes` in: the editor flags, and the rules
 * of every node and of every filler a ComponentSlot among them renders.
 */
function useSlotVisibilityContext(
  nodes: ComputedRef<ComponentNode[]>,
): ComputedRef<SlotVisibilityContext> {
  const pipe = usePipe()

  // A ComponentSlot renders the fillers the enclosing SharedNodeRef instance
  // supplied for its name, so the fillers' own rules are what decide the band.
  const sharedNodeSlots = inject<ComputedRef<Record<string, ComponentNode[]>>>(
    'sharedNodeSlots',
    computed(() => ({})),
  )
  const fillersFor = (node: ComponentNode): ComponentNode[] =>
    sharedNodeSlots.value[(node.fields?.name as string) || 'default'] ?? []

  const visibility = useVisibilityMap(
    computed(() => ({ children: nodes.value })),
    pipe.getData,
  )
  const fillerVisibility = useVisibilityMap(
    computed(() => ({
      children: nodes.value.filter((node) => node.type === 'ComponentSlot').flatMap(fillersFor),
    })),
    pipe.getData,
  )

  return computed(() => ({
    isEditor: pipe.currentMode.value === PipeDisplayMode.EDITOR,
    override: (nodeId) => pipe.visibilityOverrides.value[nodeId],
    disableAllRules: !!pipe.disableAllVisibilityRules.value,
    fillersFor,
    ruleShows: (nodeId) => (visibility.value[nodeId] ?? fillerVisibility.value[nodeId]) !== false,
  }))
}

/**
 * Reactive `slotHasVisibleChild` for one slot of the calling component — see
 * that rule's JSDoc for what the answer is for and which copies mirror it.
 */
export function useSlotHasVisibleChild(
  slotChildren: MaybeRefOrGetter<ComponentNode[] | undefined>,
): ComputedRef<boolean> {
  const nodes = computed(() => toValue(slotChildren) ?? [])
  const context = useSlotVisibilityContext(nodes)
  return computed(() => slotHasVisibleChild(nodes.value, context.value))
}

/**
 * The same answer for every slot of the calling component at once, keyed by slot name —
 * for a component whose slots are a list the author edits (ResponsiveContainer's
 * columns), where calling `useSlotHasVisibleChild` once per slot is not possible.
 */
export function useSlotsHaveVisibleChild(
  slotChildren: MaybeRefOrGetter<Record<string, ComponentNode[]> | undefined>,
): ComputedRef<Record<string, boolean>> {
  const slots = computed(() => toValue(slotChildren) ?? {})
  const nodes = computed(() => Object.values(slots.value).flat())
  const context = useSlotVisibilityContext(nodes)
  return computed(() => {
    const shown: Record<string, boolean> = {}
    for (const [name, children] of Object.entries(slots.value)) {
      shown[name] = slotHasVisibleChild(children, context.value)
    }
    return shown
  })
}
